import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { AppLayout } from '@/app/layouts/AppLayout';
import { PlatformLayout } from '@/app/layouts/PlatformLayout';
import { FeatureGate } from '@/features/billing';

import { RequireAuth, RoleHome } from './guards';
import { RouteError } from './RouteError';
import { Spinner } from '@/shared/ui/atoms/Spinner';

const page = (loader: () => Promise<{ default: React.ComponentType }>) => {
  const C = lazy(loader);
  return (
    <Suspense fallback={<Spinner fullscreen />}>
      <C />
    </Suspense>
  );
};

/** A page that needs a plan feature: shows an upgrade prompt instead of a 402 error. */
const gated = (feature: string, loader: () => Promise<{ default: React.ComponentType }>) => (
  <FeatureGate feature={feature}>{page(loader)}</FeatureGate>
);

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <RoleHome /> },
      { path: '/login', element: page(() => import('@/pages/LoginPage')) },
      {
        path: '/invitations/:token?',
        element: page(() => import('@/pages/AcceptInvitationPage'))
      },
      {
        path: '/forgot-password',
        element: page(() => import('@/pages/ForgotPasswordPage'))
      },
      {
        path: '/reset-password',
        element: page(() => import('@/pages/ResetPasswordPage'))
      },
      {
        element: <RequireAuth roles={['TENANT_ADMIN', 'MANAGER', 'MEMBER']} />,
        children: [
          {
            element: <AppLayout />,
            id: 'app-shell',
            children: [
              {
                path: '/dashboard',
                element: page(() => import('@/pages/DashboardPage'))
              },
              {
                path: '/board',
                element: page(() => import('@/pages/TaskBoardPage'))
              },
              {
                path: '/projects',
                element: page(() => import('@/pages/ProjectsPage'))
              },
              {
                path: '/projects/:id',
                element: page(() => import('@/pages/ProjectDetailPage'))
              },
              {
                path: '/team',
                element: gated('TEAM_WORKLOAD', () => import('@/pages/TeamWorkloadPage'))
              },
              {
                path: '/reports',
                element: page(() => import('@/pages/ReportsPage'))
              },
              {
                path: '/reports/hours',
                element: page(() => import('@/pages/HoursReportPage'))
              },
              {
                path: '/reports/payroll',
                element: page(() => import('@/pages/PayrollPage'))
              },
              {
                path: '/analytics',
                element: gated('ANALYTICS', () => import('@/pages/AnalyticsPage'))
              },
              {
                path: '/calendar',
                element: page(() => import('@/pages/CalendarPage'))
              },
              {
                path: '/messages',
                element: gated('MESSAGING', () => import('@/pages/MessagesPage'))
              },
              {
                path: '/settings',
                element: page(() => import('@/pages/SettingsPage'))
              }
            ]
          }
        ]
      },
      {
        element: <RequireAuth roles={['PLATFORM_ADMIN']} />,
        children: [
          {
            element: <PlatformLayout />,
            id: 'platform-shell',
            children: [
              {
                path: '/platform/tenants',
                element: page(() => import('@/pages/platform/TenantsPage'))
              },
              {
                path: '/platform/users',
                element: page(() => import('@/pages/platform/UsersPage'))
              },
              {
                path: '/platform/invitations',
                element: page(() => import('@/pages/platform/InvitationsPage'))
              },
              {
                path: '/platform/settings',
                element: page(
                  () => import('@/pages/platform/PlatformSettingsPage')
                )
              }
            ]
          }
        ]
      },
      { path: '*', element: <RoleHome /> }
    ]
  }
]);
